import { getApiBase } from "@app/services/apiConfig";

const API_BASE = getApiBase();
const AI_BASE = `${API_BASE}/api/ai`;

export type RuleSignal = {
  id: string;
  label: string;
  severity: "info" | "warning" | "critical";
  category: string;
  triggered: boolean;
  score?: number | null;
  summary?: string | null;
  value?: string | null;
  threshold?: string | null;
  sources?: string[];
  tags?: string[];
};

export type SupervisorRecommendation = {
  symbol: string;
  action: "buy" | "sell" | "hold";
  score: number;
  confidence: number;
  reasons: string[];
};

export type RolePlanRole = {
  id: string;
  label: string;
  description?: string | null;
  defaultPlan?: string | null;
  aiCapabilities: string[];
  permissions: string[];
};

export type RolePlanTier = {
  id: string;
  label: string;
  description?: string | null;
  aiLimits: Record<string, unknown>;
  allowedRoles: string[];
};

export type RolePlanResponse = {
  generatedAt: string;
  version?: string | null;
  roles: RolePlanRole[];
  plans: RolePlanTier[];
};

export type SupervisorDashboardResponse = {
  generatedAt: string;
  overallStatus: "info" | "warning" | "critical";
  summary?: string | null;
  recommendations: SupervisorRecommendation[];
  triggeredSignals: RuleSignal[];
  rolePlan: RolePlanResponse;
  context: Record<string, unknown>;
};

export type PerformanceMetric = {
  id: string;
  label: string;
  value: number;
  unit?: string | null;
  change?: number | null;
  notes?: string | null;
};

export type KnowledgeVersionEntry = {
  path: string;
  sha256: string;
  sizeBytes: number;
  modifiedAt: string;
  category?: string | null;
};

export type KnowledgeVersionSnapshot = {
  versionId: string;
  generatedAt: string;
  fileCount: number;
  categories: Record<string, number>;
  files: KnowledgeVersionEntry[];
};

export type DecisionPerformanceSummary = {
  total: number;
  executed: number;
  approved: number;
  rejected: number;
  pending: number;
  successRate: number;
  averageConfidence?: number | null;
};

export type TradePerformanceSummary = {
  total: number;
  executed: number;
  cancelled: number;
  failed: number;
  winRate?: number | null;
  grossPnl?: number | null;
  netPnl?: number | null;
};

export type AiPerformanceReport = {
  generatedAt: string;
  periodHours: number;
  knowledgeVersion: KnowledgeVersionSnapshot;
  decisionSummary: DecisionPerformanceSummary;
  tradeSummary: TradePerformanceSummary;
  metrics: PerformanceMetric[];
  notes: string[];
};

export type PermissionProfile = {
  roleId: string;
  roleLabel?: string | null;
  planId: string;
  planLabel?: string | null;
  authorityLevel: "full" | "guarded" | "review";
  automationMode: "disabled" | "semi" | "full";
  capabilities: string[];
  allowedActions: string[];
  restrictedActions: string[];
  limits: Record<string, unknown>;
  generatedAt: string;
};

export type RollbackStep = {
  id: string;
  label: string;
  description?: string | null;
  priority: "low" | "medium" | "high";
};

export type RollbackPlan = {
  planId: string;
  summary: string;
  steps: RollbackStep[];
};

export type AutomationRiskLevel = "low" | "medium" | "high" | "critical";

export type AutomationGuardRequest = {
  action: string;
  description?: string;
  roleId?: string;
  planId?: string;
  riskLevel: AutomationRiskLevel;
  performanceWindowHours?: number;
  dryRun?: boolean;
  requestedBy?: string;
  context?: Record<string, unknown>;
};

export type AutomationGuardResponse = {
  allowed: boolean;
  requiresManualReview: boolean;
  riskLevel: AutomationRiskLevel;
  permissionProfile: PermissionProfile;
  triggeredPolicies: string[];
  guardrails: string[];
  environment: Record<string, unknown>;
  rollbackPlan: RollbackPlan;
  generatedAt: string;
  notes: string[];
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === "string") {
        message = payload.detail;
      }
    } catch (error) {
      // ignore parsing issues
    }
    throw new Error(message);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export async function fetchSupervisorDashboard() {
  const response = await fetch(`${AI_BASE}/supervisor-dashboard`);
  return handleResponse<SupervisorDashboardResponse>(response);
}

export async function fetchPerformanceReport(periodHours: number) {
  const response = await fetch(`${AI_BASE}/performance?periodHours=${encodeURIComponent(periodHours)}`);
  return handleResponse<AiPerformanceReport>(response);
}

export async function evaluateAutomationGuard(payload: AutomationGuardRequest) {
  const response = await fetch(`${AI_BASE}/automation/guard`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse<AutomationGuardResponse>(response);
}

